import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Pencil, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

/** Staff: rename an existing stock product so its name matches what the quote tool looks up. */
export function RenameToMatch({ item, onRenamed }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [all, setAll] = useState(null);
  const [busy, setBusy] = useState(null);
  const expected = item.expected_name || item.names?.[0];
  const slug = item.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase();

  useEffect(() => {
    if (!open || all) return;
    fetch(`${API_URL}/api/stock/products`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setAll(rows.map((r) => ({ id: r.id, name: r.name, category: r.category }))))
      .catch(() => setAll([]));
  }, [open, all]);

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return item.suggestions || [];
    return (all || []).filter((p) => p.name.toLowerCase().includes(t)).slice(0, 8);
  }, [q, all, item.suggestions]);

  const rename = async (p) => {
    if (!window.confirm(`Rename "${p.name}" to "${expected}"?\n\nThe quote tool will then find it. Stock history keeps the new name.`)) return;
    setBusy(p.id);
    try {
      const res = await fetch(`${API_URL}/api/stock/products/${p.id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: expected }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Rename failed');
      toast.success(`Renamed to "${expected}" — quote tool now linked`);
      setOpen(false);
      onRenamed?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-900 bg-white border border-amber-300 rounded-full px-2 py-0.5 hover:bg-amber-100" data-testid={`rename-to-match-${slug}`}>
        <Pencil className="w-3 h-3" /> Rename to match
      </button>
    );
  }
  return (
    <div className="w-full mt-1 rounded-lg border border-amber-300 bg-white p-2 text-xs shadow-sm" data-testid={`rename-picker-${slug}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-amber-900">Pick the product to rename to <strong>&quot;{expected}&quot;</strong></span>
        <button type="button" onClick={() => setOpen(false)} className="p-0.5 text-gray-400 hover:text-gray-700" aria-label="Close" data-testid={`rename-picker-close-${slug}`}><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="relative mb-1.5">
        <Search className="w-3.5 h-3.5 absolute left-2 top-1.5 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={item.suggestions?.length ? 'Closest matches shown — or search all products…' : 'Search products…'} className="w-full h-7 pl-7 pr-2 border rounded text-xs" data-testid={`rename-picker-search-${slug}`} autoFocus />
      </div>
      {q && !all ? <div className="text-gray-500 py-1 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
        : list.length === 0 ? <div className="text-gray-500 py-1" data-testid={`rename-picker-empty-${slug}`}>No matching products{q ? '' : ' — type to search all'}.</div>
          : (
            <ul className="divide-y">
              {list.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 py-1">
                  <span className="truncate"><span className="font-semibold text-gray-900">{p.name}</span>{p.category && <span className="text-gray-400"> · {p.category}</span>}</span>
                  <button type="button" disabled={busy === p.id} onClick={() => rename(p)} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-white bg-amber-600 hover:bg-amber-700 rounded px-2 py-0.5 disabled:opacity-60" data-testid={`rename-pick-${p.id}`}>
                    {busy === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pencil className="w-3 h-3" />} Rename
                  </button>
                </li>
              ))}
            </ul>
          )}
    </div>
  );
}
